import React from 'react';
import { Container, Typography, Switch, FormControlLabel, Button, Divider, Dialog, DialogTitle, DialogContent, DialogActions, FormGroup, Checkbox, TextField, Chip, Autocomplete, Box, IconButton, Tooltip } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import Menu from '../components/Menu';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import Share from '../components/dashboard/Share';
import Hashrate from '../components/dashboard/Hashrate';
import Temperature from '../components/dashboard/Temperature';
import Fan from '../components/dashboard/Fan';
import Pool from '../components/dashboard/Pool';
import Logs from '../components/dashboard/Logs';
import Info from '../components/dashboard/Info';
import GraphDevice from '../components/dashboard/GraphDevice';
import Worktime from '../components/dashboard/Worktime';
import { useParams, useNavigate } from 'react-router-dom';
import DeviceCard from '../components/device/DeviceCard';
import api from '../utils/apiClient';
import { API_BASE, DEVICE_API } from '../utils/apiConfig';
import { getErrorMessage } from '../utils/errorMessage';
import { useNotifications } from '../context/NotificationContext';
import NotificationContainer from '../components/NotificationContainer';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { isHosterOrAbove } from '../utils/roles';

const ResponsiveGridLayout = WidthProvider(Responsive);

const Device = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const { showError, showSuccess, showInfo } = useNotifications();
  const { t } = useTranslation('device');

  // Проверяем роль пользователя для доступа к админ функциям
  const isAdminOrManager = isHosterOrAbove(user?.role);
  const [isEditing, setIsEditing] = React.useState(false);
  const [refreshInterval, setRefreshInterval] = React.useState(60000); // 1 минута по умолчанию
  const [layout, setLayout] = React.useState([
    { w: 4, h: 8, x: 0, y: 0, i: 'info' },
    { w: 8, h: 8, x: 4, y: 0, i: 'graphDevice' },
    { w: 6, h: 6, x: 6, y: 8, i: 'worktime' },
    { w: 3, h: 6, x: 0, y: 8, i: 'temperature' },
    { w: 3, h: 6, x: 3, y: 8, i: 'fan' },
    { w: 3, h: 7, x: 0, y: 14, i: 'share' },
    { w: 9, h: 7, x: 3, y: 14, i: 'pool' },
    { w: 12, h: 7, x: 0, y: 21, i: 'logs' },
    { w: 12, h: 4, x: 0, y: 28, i: 'hashrate' },
  ]);

  const layouts = React.useMemo(() => {
    const smLayout = [
      { w: 1, h: 8, x: 0, y: 0, i: 'info' },
      { w: 1, h: 8, x: 0, y: 8, i: 'graphDevice' },
      { w: 1, h: 6, x: 0, y: 16, i: 'temperature' },
      { w: 1, h: 6, x: 0, y: 22, i: 'fan' },
      { w: 1, h: 6, x: 0, y: 28, i: 'worktime' },
      { w: 1, h: 7, x: 0, y: 34, i: 'share' },
      { w: 1, h: 7, x: 0, y: 41, i: 'pool' },
      { w: 1, h: 7, x: 0, y: 48, i: 'logs' },
      { w: 1, h: 4, x: 0, y: 55, i: 'hashrate' },
    ];
    const mdLayout = [
      { w: 2, h: 8, x: 0, y: 0, i: 'info' },
      { w: 2, h: 8, x: 0, y: 8, i: 'graphDevice' },
      { w: 1, h: 6, x: 0, y: 16, i: 'temperature' },
      { w: 1, h: 6, x: 1, y: 16, i: 'fan' },
      { w: 2, h: 6, x: 0, y: 22, i: 'worktime' },
      { w: 1, h: 7, x: 0, y: 28, i: 'share' },
      { w: 1, h: 7, x: 1, y: 28, i: 'pool' },
      { w: 2, h: 7, x: 0, y: 35, i: 'logs' },
      { w: 2, h: 4, x: 0, y: 42, i: 'hashrate' },
    ];
    return { lg: layout, md: mdLayout, sm: smLayout, xs: smLayout };
  }, [layout]);
  const [deviceData, setDeviceData] = React.useState(null);
  const [selectedRows, setSelectedRows] = React.useState([]);
  const [selectAll, setSelectAll] = React.useState(false);
  const [openSettingsDialog, setOpenSettingsDialog] = React.useState(false);
  const [openEditDialog, setOpenEditDialog] = React.useState(false);
  const [openValuesDialog, setOpenValuesDialog] = React.useState(false);
  const [valuesForm, setValuesForm] = React.useState({});
  const [valuesExisting, setValuesExisting] = React.useState(null);
  const [isSavingValues, setIsSavingValues] = React.useState(false);
  const [isDeletingValues, setIsDeletingValues] = React.useState(false);
  const [editValues, setEditValues] = React.useState(null);
  const [isSavingEdit, setIsSavingEdit] = React.useState(false);
  const [ownersOptions, setOwnersOptions] = React.useState([]);
  const [tagsOptions, setTagsOptions] = React.useState([]);
  const [hostingOptions, setHostingOptions] = React.useState([]);
  const [locationOptions, setLocationOptions] = React.useState([]);
  const [modelsOptions, setModelsOptions] = React.useState([]);
  const [alertRules, setAlertRules] = React.useState([]);
  const [alertRulesCount, setAlertRulesCount] = React.useState(0);
  const [alertRulesLoading, setAlertRulesLoading] = React.useState(false);
  const [openDateRangeDialog, setOpenDateRangeDialog] = React.useState(false);
  const [dateRange, setDateRange] = React.useState({
    start: dayjs().subtract(1, 'day'), // Последние 24 часа
    end: dayjs()
  });
  const [hoveredTimestamp, setHoveredTimestamp] = React.useState(null);
  // Период карточки: те же пресеты, что на дашборде
  const PERIOD_PRESETS = { '1h': [1, 'hour'], '24h': [24, 'hour'], '3d': [3, 'day'], '30d': [30, 'day'] };
  const [periodPreset, setPeriodPreset] = React.useState('24h');
  const [rangeNonce, setRangeNonce] = React.useState(0);
  // Строка сводки работы по этому асику (мощность, норма, кВт·ч, сумма), тариф площадки, окна сбоев мониторинга
  const [workRow, setWorkRow] = React.useState(null);
  const [kwhPrice, setKwhPrice] = React.useState(null);
  const [outages, setOutages] = React.useState([]);
  // События по асику (алерты, возвраты в норму, потери связи); null — ещё загружаются
  const [deviceEvents, setDeviceEvents] = React.useState(null);
  const [visibleModules, setVisibleModules] = React.useState({
    info: true,
    graphDevice: true,
    worktime: true,
    temperature: true,
    fan: true,
    share: true,
    pool: true,
    logs: true,
    hashrate: false,
  });

  // Логи устройства
  const [deviceLogs, setDeviceLogs] = React.useState([]);
  const [deviceLogsLoading, setDeviceLogsLoading] = React.useState(false);
  const [deviceLogsTotal, setDeviceLogsTotal] = React.useState(0);

  // Данные графика устройства
  const [chartData, setChartData] = React.useState(null);
  const [chartDataLoading, setChartDataLoading] = React.useState(false);

  const APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Защита от запоздавших ответов: при быстрой смене асика или периода ответ на старый запрос мог прийти
  // позже нового и подменить данные. Принимаем только ответ на последний запрос.
  const deviceSeq = React.useRef(0);
  const chartSeq = React.useRef(0);

  // Функция для правильного определения статуса устройства на основе uptime_data
  // Иерархия статусов:
  // 1. Специальные статусы (delete, repair, maintenance и т.д.) - если открыты сейчас (ended_at = null).
  //    Закрытые (прошлый ремонт) видны только на полосе «Время работы», а не в текущем статусе.
  // 2. Online/Offline - показываем только если нет специальных статусов
  const getDeviceStatus = (uptimeData) => {
    if (!uptimeData || !Array.isArray(uptimeData) || uptimeData.length === 0) {
      return null; // Нет данных о статусе
    }

    // Сортируем записи по времени и берем самую новую по started_at
    const sortedUptimeData = [...uptimeData].sort((a, b) => {
      const timeA = new Date(a.created_at || a.started_at);
      const timeB = new Date(b.created_at || b.started_at);
      return timeB - timeA; // Сортировка по убыванию (самая новая первая)
    });

    // 1. Ищем специальные статусы (не online/offline) среди всех записей
    // Приоритет: delete > repair/maintenance/etc
    const specialStatuses = sortedUptimeData.filter(record =>
      record.ended_at === null &&
      typeof record.status === 'string' &&
      record.status.toLowerCase() !== 'online' &&
      record.status.toLowerCase() !== 'offline'
    );

    if (specialStatuses.length > 0) {
      // Проверяем delete (высший приоритет)
      const deleteStatus = specialStatuses.find(record =>
        record.status.toLowerCase() === 'delete'
      );
      if (deleteStatus) {
        return 'delete';
      }

      // Возвращаем самый новый специальный статус
      return specialStatuses[0].status.toLowerCase();
    }

    // 2. Если нет специальных статусов, используем активный online/offline
    const activeStatuses = sortedUptimeData.filter(record => record.ended_at === null);

    if (activeStatuses.length > 0) {
      const onlineOfflineStatus = activeStatuses.find(record =>
        record.status === true ||
        record.status === false ||
        (typeof record.status === 'string' &&
         (record.status.toLowerCase() === 'online' || record.status.toLowerCase() === 'offline'))
      );

      if (onlineOfflineStatus) {
        if (onlineOfflineStatus.status === true ||
            (typeof onlineOfflineStatus.status === 'string' &&
             onlineOfflineStatus.status.toLowerCase() === 'online')) {
          return true; // Устройство в онлайне
        } else {
          return false; // Устройство в оффлайне
        }
      }
    }

    // 3. Если нет активных статусов, берем самый новый статус
    const latestUptime = sortedUptimeData[0];

    if (typeof latestUptime.status === 'string') {
      const normalizedStatus = latestUptime.status.toLowerCase();

      if (normalizedStatus === 'online') {
        return true;
      } else if (normalizedStatus === 'offline') {
        return false;
      }
    }

    // Обратная совместимость с boolean статусами
    if (latestUptime.status === true) {
      return true;
    } else if (latestUptime.status === false) {
      return false;
    }

    return null; // Неопределенный статус
  };

  const createDeviceData = (model, status, algorithm, mac, hashrate, temperature) => {
    return { model, status, algorithm, mac, hashrate, temperature };
  };

  // Функция для получения started_at из открытого диапазона для online/offline
  const getStatusStartedAt = (uptimeData, targetStatus) => {
    if (!uptimeData || !Array.isArray(uptimeData) || uptimeData.length === 0) {
      return null;
    }

    // Нормализуем целевой статус
    const normalizedTarget = targetStatus === true || targetStatus === 'online' ? 'online' : 'offline';

    // Ищем запись с ended_at === null и нужным статусом
    const activeRecord = uptimeData.find(record => {
      if (record.ended_at !== null) return false;

      // Нормализуем статус записи
      let recordStatus;
      if (record.status === true || (typeof record.status === 'string' && record.status.toLowerCase() === 'online')) {
        recordStatus = 'online';
      } else if (record.status === false || (typeof record.status === 'string' && record.status.toLowerCase() === 'offline')) {
        recordStatus = 'offline';
      } else {
        return false; // Специальные статусы не учитываем
      }

      return recordStatus === normalizedTarget;
    });

    return activeRecord?.started_at || null;
  };

  // Функция для расчета длительности статуса
  const getStatusDuration = (startedAt) => {
    if (!startedAt) return null;

    const now = new Date();
    const startDate = new Date(startedAt);
    const diffMs = now - startDate;

    // Если разница отрицательная или некорректная, возвращаем null
    if (diffMs < 0 || isNaN(diffMs)) return null;

    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays}д ${diffHours % 24}ч`;
    } else if (diffHours > 0) {
      return `${diffHours}ч ${diffMinutes % 60}м`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes}м`;
    } else {
      return `${diffSeconds}с`;
    }
  };

  const deviceRows = [
    createDeviceData('Bitmain Antminer l9', 'Online', 'SHA-256', '8A:2F:6C:F3:5C:CD', '17Gh/s', ['53°', '56°', '52°']),
    createDeviceData('Antminer s19 pro', 'Online', 'SHA-256', '8A:0A:21:C7:E2:ED', '120Th/s', ['52°', '56°', '54°']),
    createDeviceData('Antminer s19', 'Online', 'SHA-256', '8A:5E:01:3E:07:DA', '95Th/s', ['93°', '86°', '92°']),
    createDeviceData('Bitmain Antminer l9', 'Offline (1h)', 'SHA-256', '8A:45:43:83:33:3F', '17.1Gh/s', ['63°', '26°', '12°']),
    createDeviceData('Bitmain Antminer l9', 'В ремонте', 'SHA-256', '8A:7E:3E:55:9A:E0', '11.2Gh/s', ['53°', '56°', '52°']),
  ];

  const filteredRows = deviceRows.filter((row) => {
    return true; // Удаление логики фильтрации
  });

  // Функция для загрузки данных устройства
  const fetchDeviceData = async (isAutoUpdate = false) => {
    const seq = ++deviceSeq.current;
    try {
      const chartUrl = `${DEVICE_API}/device/chart`;

      if (!id) {
        showError(t('deviceIdMissing'));
        return;
      }

      // При автообновлении сдвигаем временное окно к текущему времени
      let start, end;
      if (isAutoUpdate && dateRange?.start && dateRange?.end) {
        const now = dayjs();
        const windowDuration = dateRange.end.diff(dateRange.start, 'millisecond');
        end = now;
        start = now.subtract(windowDuration, 'millisecond');

        // Обновляем состояние dateRange для отображения актуального диапазона
        setDateRange({ start, end });
      } else {
        // Используем точное время из выбранного диапазона
        start = dateRange.start;
        end = dateRange.end;
      }

      const startDateIso = start && start.toDate().toISOString();
      const endDateIso = end && end.toDate().toISOString();

      const response = await api.get(`${chartUrl}/${id}`, {
        params: {
          start_date: startDateIso,
          end_date: endDateIso,
        },
      });

      // Преобразуем данные из нового формата в ожидаемый формат
      const transformedData = {
        device: response.data.device,
        hosting: response.data.hosting,
        location: response.data.location,
        pools: response.data.pools,
        uptime_data: response.data.uptime, // Переименовываем uptime в uptime_data
        hashrate_data: response.data.metrics, // Переименовываем metrics в hashrate_data
        latest_metrics: {
          // Новый формат latest_metrics уже плоский и подходит для Temperature и Fan
          temperature: response.data.latest_metrics, // Передаем весь объект с temperature0, temperature1, и т.д.
          fans: response.data.latest_metrics, // Передаем весь объект с fan_speed0, fan_speed1, и т.д.
          shares: {
            accepted: response.data.device?.accepted_shares,
            rejected: response.data.device?.rejected_shares,
          }
        }
      };

      if (seq !== deviceSeq.current) return; // пришёл ответ на устаревший запрос
      setDeviceData(transformedData);
      showSuccess(t('dataLoaded'));
    } catch (error) {
      if (error.response) {
        showError(t('errorStatus', { status: error.response.status, message: getErrorMessage(error, t('unknownError')) }));
      } else {
        showError(t('networkError'));
      }
    }
  };

  // Функция для загрузки данных графика устройства
  const fetchChartData = async (isAutoUpdate = false) => {
    const seq = ++chartSeq.current;
    try {
      if (!id) {
        return;
      }

      setChartDataLoading(true);

      // При автообновлении используем обновленный dateRange (уже сдвинутый в fetchDeviceData)
      // При обычном обновлении используем текущий dateRange
      const start = dateRange.start;
      const end = dateRange.end;

      const startDateIso = start && start.toDate().toISOString();
      const endDateIso = end && end.toDate().toISOString();

      const chartApiUrl = `${DEVICE_API}/device/chart/${id}`;

      const response = await api.get(chartApiUrl, {
        params: {
          start_date: startDateIso,
          end_date: endDateIso,
        },
      });

      // Преобразуем данные из нового формата
      const transformedChartData = {
        hashrate_data: response.data.metrics, // Переименовываем metrics в hashrate_data
        uptime_data: response.data.uptime, // Переименовываем uptime в uptime_data
      };

      if (seq !== chartSeq.current) return; // пришёл ответ на устаревший запрос
      setChartData(transformedChartData);
      
    } catch (error) {
      if (error.response) {
        // Ошибка загрузки данных графика
      } else {
        // Ошибка сети при загрузке данных графика
      }
    } finally {
      setChartDataLoading(false);
    }
  };

  // Загрузка правил алертов для устройства
  const fetchAlertRules = async () => {
    if (!id) return;
    try {
      setAlertRulesLoading(true);
      const baseUrl = API_BASE;
      const url = `${baseUrl}value_metrics/${id}`;
      const resp = await api.get(url);
      const payload = resp?.data || {};
      const success = payload?.success === true || payload?.ok === true;
      if (success) {
        const data = Array.isArray(payload.data) ? payload.data : (Array.isArray(payload.results) ? payload.results : []);
        const count = typeof payload.count === 'number' ? payload.count : data.length;
        setAlertRules(data);
        setAlertRulesCount(count);
      } else {
        // В некоторых случаях API может не возвращать success, но возвращать массив напрямую
        const data = Array.isArray(payload) ? payload : [];
        setAlertRules(data);
        setAlertRulesCount(data.length);
      }
    } catch (e) {
      // Ошибка загрузки правил алертов
    } finally {
      setAlertRulesLoading(false);
    }
  };

  React.useEffect(() => {
    // другой асик — данные прежнего не должны остаться на экране, даже если новая загрузка не удастся
    setDeviceData(null);
    setChartData(null);
    setDeviceLogs([]);
    setDeviceLogsTotal(0);
    setAlertRules([]);
    setWorkRow(null);
    setDeviceEvents(null);
    fetchDeviceData();
    fetchChartData();
    // Начальная загрузка логов устройства
    void fetchDeviceLogs({ limit: 5, offset: 0 });
    // Загрузка правил алертов
    void fetchAlertRules();
  }, [id]);

  // Функция для принудительного обновления данных
  const handleManualRefresh = () => {
    showInfo(t('refreshing'));
    fetchDeviceData();
    fetchChartData();
  };

  // Функция для изменения интервала обновления
  const handleSetRefreshInterval = (newInterval) => {
    setRefreshInterval(newInterval);
    
    if (newInterval === 0) {
      showInfo(t('autoRefreshOff'));
    } else {
      const intervalSeconds = Math.round(newInterval / 1000);
      showSuccess(t('intervalSet', { seconds: intervalSeconds }));
    }
  };

  const handlePresetChange = (preset) => {
    const cfg = PERIOD_PRESETS[preset];
    if (!cfg) return;
    setPeriodPreset(preset);
    setDateRange({ start: dayjs().subtract(cfg[0], cfg[1]), end: dayjs() });
    setRangeNonce((n) => n + 1);
  };

  // После смены пресета состояние dateRange уже новое — перезагружаем данные с ним
  React.useEffect(() => {
    if (rangeNonce === 0) return;
    fetchDeviceData();
    fetchChartData();
  }, [rangeNonce]);

  const fetchWorkRow = async () => {
    try {
      const resp = await api.get(`${APP_API_BASE_URL}work_hours/summary`, {
        params: { start_date: dateRange.start.toDate().toISOString(), end_date: dateRange.end.toDate().toISOString() },
      });
      const row = (resp?.data?.devices || []).find((d) => d.id === id) || null;
      setWorkRow(row);
      setKwhPrice(row ? resp.data.kwh_price?.[row.hosting_id] ?? null : null);
      setOutages((resp?.data?.outages || []).filter((o) => o.shares && o.shares[id] !== undefined));
    } catch (e) {
      setWorkRow(null);
      setOutages([]);
    }
  };

  const fetchDeviceEvents = async () => {
    try {
      const resp = await api.get(`${DEVICE_API}/device/events/${id}`, { params: { limit: 15 } });
      setDeviceEvents(resp?.data?.events || []);
    } catch (e) {
      setDeviceEvents([]);
    }
  };

  React.useEffect(() => {
    if (!id) return;
    void fetchWorkRow();
    void fetchDeviceEvents();
  }, [id, rangeNonce]);

  // Объект с функциями для передачи в Menu
  const refreshFunctions = {
    refreshData: handleManualRefresh,
    setRefreshInterval: handleSetRefreshInterval
  };

  React.useEffect(() => {
    const savedLayout = localStorage.getItem('deviceLayout');
    if (savedLayout) {
      setLayout(JSON.parse(savedLayout));
    }
    
    // Загружаем сохраненные настройки модулей
    const savedModules = localStorage.getItem('deviceVisibleModules');
    if (savedModules) {
      setVisibleModules(JSON.parse(savedModules));
    }
    
    setTimeout(() => {
      updateComponentSizes();
    }, 300);
  }, []);

  // Автоматическое обновление данных по интервалу
  React.useEffect(() => {
    if (refreshInterval <= 0) return; // Если интервал 0, отключаем автообновление
    
    const intervalId = setInterval(async () => {
      await fetchDeviceData(true); // true = автообновление, сдвигаем временное окно
      await fetchChartData(true);
    }, refreshInterval);

    return () => {
      clearInterval(intervalId);
    };
  }, [refreshInterval, id]);

  React.useEffect(() => {
    updateComponentSizes();
  }, [layout]);

  React.useLayoutEffect(() => {
    updateComponentSizes();
  }, [layout]);

  const updateComponentSizes = () => {
    const event = new Event('resize');
    window.dispatchEvent(event);
  };

  // Загрузка логов устройства
  const fetchDeviceLogs = async (opts = {}) => {
    if (!id) return;
    const limit = Number.isInteger(opts.limit) ? opts.limit : 5;
    const offset = Number.isInteger(opts.offset) ? opts.offset : 0;
    const params = { limit, offset, device_id: id };
    try {
      setDeviceLogsLoading(true);
      const resp = await api.get(`${APP_API_BASE_URL}logs/device`, { params });
      const d = resp?.data;
      const list = Array.isArray(d) ? d : (Array.isArray(d?.logs) ? d.logs : (Array.isArray(d?.data) ? d.data : []));
      // count в ответе — размер страницы, а не всего журнала; есть ли продолжение, говорит has_more
      const more = typeof d?.has_more === 'boolean' ? d.has_more : list.length === limit;
      const total = offset + list.length + (more ? 1 : 0);
      if (offset > 0) {
        setDeviceLogs((prev) => Array.isArray(prev) ? [...prev, ...list] : list);
      } else {
        setDeviceLogs(list);
      }
      setDeviceLogsTotal(total);
    } catch (e) {
      // Ошибка загрузки логов устройства
    } finally {
      setDeviceLogsLoading(false);
    }
  };

  const handleLoadMoreDeviceLogs = async () => {
    const current = Array.isArray(deviceLogs) ? deviceLogs.length : 0;
    await fetchDeviceLogs({ limit: 20, offset: current });
  };

  const handleResize = (layout, oldItem, newItem) => {
    if (!isEditing) return;

    if (newItem.i === 'a') {
      const container = document.getElementById('heatmap-container');
      if (container) {
        const event = new Event('resize');
        window.dispatchEvent(event);
      }
    }
  };

  const handleResizeStop = (newLayout) => {
    if (!isEditing) return;

    setLayout(newLayout);
    setTimeout(() => {
      updateComponentSizes();
    }, 200);
  };

  const handleDragStop = (newLayout) => {
    if (!isEditing) return;

    setLayout(newLayout);
    updateComponentSizes();
  };

  // сбрасываем редактирование при переходе на мобилку
  React.useEffect(() => {
    if (isMobile && isEditing) setIsEditing(false);
  }, [isMobile]);

  const handleSwitchChange = (event) => {
    const isEditingMode = event.target.checked;
    setIsEditing(isEditingMode);
  };

  const saveLayout = () => {
    localStorage.setItem('deviceLayout', JSON.stringify(layout));
    setIsEditing(false);
  };

  // Функции для управления настройками модулей
  const handleSettingsDialogOpen = () => {
    setOpenSettingsDialog(true);
    showInfo(t('settingsOpened'));
  };
  // Открытие диалога редактирования из Info
  const handleInfoEditOpen = () => {
    if (!deviceData?.device) return;
    const d = deviceData.device;
    
    // Обрабатываем теги из устройства
    let deviceTags = [];
    if (Array.isArray(d.tags)) {
      deviceTags = d.tags;
    } else if (typeof d.tags === 'string' && d.tags.trim() !== '') {
      // Если теги приходят как строка, парсим их
      deviceTags = d.tags.replace(/^\(|\)$/g, '').split(',').map(t => t.replace(/^\s*\"|\"\s*$/g, '').trim()).filter(Boolean);
    } else if (typeof d.tags === 'object' && d.tags !== null) {
      // Если теги приходят как объект { tag: true }, преобразуем в массив
      deviceTags = Object.keys(d.tags).filter(key => d.tags[key] === true);
    }
    
    
    setEditValues({
      mac: d.mac || '',
      serial_number: d.serial_number || '',
      sn: d.sn || '',
      owner_name: d.owner_name || '',
      owner_id: d.owner_id || '',
      algorithm: d.algorithm || '',
      contract_amount: d.contract_amount ?? '',
      location_id: deviceData?.location?.id || '',
      location_name: deviceData?.location?.name || '',
      hosting_id: deviceData?.hosting?.id || '',
      hosting_name: deviceData?.hosting?.name || '',
      hashrate_unit: d.hashrate_unit || '',
      tags: deviceTags,
      model_id: d.model_id || '',
      model_name: d.model || ''
    });
    setOpenEditDialog(true);
    // Запрос списков включая теги
    void fetchEditLists();
  };

  const handleValuesSettingsOpen = () => {
    // Подготовка формы из загруженных метрик
    const first = Array.isArray(alertRules) && alertRules.length > 0 ? alertRules[0] : null;
    setValuesExisting(first);
    const initial = {
      hashrate_min: first?.hashrate_min ?? '',
      hashrate_max: first?.hashrate_max ?? '',
      temperature0_min: first?.temperature0_min ?? '',
      temperature0_max: first?.temperature0_max ?? '',
      temperature1_min: first?.temperature1_min ?? '',
      temperature1_max: first?.temperature1_max ?? '',
      temperature2_min: first?.temperature2_min ?? '',
      temperature2_max: first?.temperature2_max ?? '',
      temperature3_min: first?.temperature3_min ?? '',
      temperature3_max: first?.temperature3_max ?? '',
      temperature4_min: first?.temperature4_min ?? '',
      temperature4_max: first?.temperature4_max ?? '',
      temperature5_min: first?.temperature5_min ?? '',
      temperature5_max: first?.temperature5_max ?? '',
      fan_speed0_min: first?.fan_speed0_min ?? '',
      fan_speed0_max: first?.fan_speed0_max ?? '',
      fan_speed1_min: first?.fan_speed1_min ?? '',
      fan_speed1_max: first?.fan_speed1_max ?? '',
      fan_speed2_min: first?.fan_speed2_min ?? '',
      fan_speed2_max: first?.fan_speed2_max ?? '',
      fan_speed3_min: first?.fan_speed3_min ?? '',
      fan_speed3_max: first?.fan_speed3_max ?? '',
    };
    setValuesForm(initial);
    setOpenValuesDialog(true);
  };

  const handleValuesSettingsClose = () => {
    setOpenValuesDialog(false);
  };

  const handleValuesInputChange = (field, value) => {
    setValuesForm((prev) => ({ ...prev, [field]: value === '' ? '' : Number(value) }));
  };

  const handleValuesSave = async () => {
    if (!id) return;
    try {
      setIsSavingValues(true);
      const baseUrl = process.env.REACT_APP_API_BASE_URL || process.env.APP_API_BASE_URL || '';
      const method = valuesExisting ? 'put' : 'post';
      const url = method === 'post' ? `${baseUrl}value_metrics` : `${baseUrl}value_metrics/${valuesExisting?.id}`;
      const payload = {
        device_id: id,
        ...valuesForm,
      };
      await api[method](url, payload);
      showSuccess(t('valuesSaved'));
      await fetchAlertRules();
      setOpenValuesDialog(false);
    } catch (e) {
      // Ошибка сохранения настроек значений
      showError(t('valuesSaveError'));
    } finally {
      setIsSavingValues(false);
    }
  };

  const handleValuesDelete = async () => {
    if (!id) return;
    try {
      setIsDeletingValues(true);
      const baseUrl = API_BASE;
      const url = `${baseUrl}value_metrics/${valuesExisting?.id}`;
      await api.delete(url);
      showSuccess(t('valuesDeleted'));
      setAlertRules([]);
      setAlertRulesCount(0);
      setOpenValuesDialog(false);
    } catch (e) {
      // Ошибка удаления настроек значений
      showError(t('valuesDeleteError'));
    } finally {
      setIsDeletingValues(false);
    }
  };

  const handleEditCancel = () => {
    setOpenEditDialog(false);
  };

  const handleEditSave = async () => {
    if (!deviceData?.device?.id) {
      showError(t('deviceIdNotFound'));
      return;
    }
    try {
      setIsSavingEdit(true);

      // Преобразуем теги в массив строк
      const tagsArray = Array.isArray(editValues.tags)
        ? editValues.tags.filter(t => String(t || '').trim() !== '')
        : [];

      const payload = {
        device_id: deviceData.device.id,
        owner_id: editValues.owner_id || null,
        hosting_id: editValues.hosting_id || null,
        location: editValues.location_id || null,
        mac: editValues.mac || null,
        serial_number: editValues.serial_number || null,
        psu_sn: editValues.sn || null,
        tags: tagsArray,
        algorithm: editValues.algorithm || null,
        model_id: editValues.model_id || null,
        hashrate_unit: editValues.hashrate_unit || null,
        contract_amount: editValues.contract_amount && editValues.contract_amount.toString().trim() !== '' ? editValues.contract_amount : null,
      };

      const response = await api.post(`${API_BASE}data_device/edit`, payload);

      const responseData = response.data;
      
      // Обрабатываем ответ API
      if (responseData.success === false) {
        // Формируем единое сообщение с предупреждениями
        let message = responseData.message || t('devicePartialUpdate');
        
        if (responseData.warnings && Array.isArray(responseData.warnings)) {
          const warningMessages = responseData.warnings.map(warning => warning.message).join('; ');
          message += `. ${t('deviceUpdateWarnings', { warnings: warningMessages })}`;
        }
        
        showError(message);
      } else {
        showSuccess(t('deviceUpdated'));
      }

      // Обновляем только те поля, которые были успешно обновлены
      if (responseData.updated_fields && Array.isArray(responseData.updated_fields)) {
        setDeviceData((prev) => {
          if (!prev) return prev;
          
          const updatedDevice = { ...prev.device };
          const updatedHosting = { ...prev.hosting };
          const updatedLocation = { ...prev.location };
          
          // Обновляем только поля из updated_fields
          responseData.updated_fields.forEach(field => {
            switch (field) {
              case 'mac':
                updatedDevice.mac = editValues.mac;
                break;
              case 'serial_number':
                updatedDevice.serial_number = editValues.serial_number;
                break;
              case 'psu_sn':
                updatedDevice.sn = editValues.sn;
                break;
              case 'owner_id':
                updatedDevice.owner_id = editValues.owner_id;
                updatedDevice.owner_name = editValues.owner_name;
                break;
              case 'hosting_id':
                if (editValues.hosting_id) {
                  updatedHosting.id = editValues.hosting_id;
                  updatedHosting.name = editValues.hosting_name;
                }
                break;
              case 'location':
                if (editValues.location_id) {
                  updatedLocation.id = editValues.location_id;
                  updatedLocation.name = editValues.location_name;
                }
                break;
              case 'algorithm':
                updatedDevice.algorithm = editValues.algorithm;
                break;
              case 'hashrate_unit':
                updatedDevice.hashrate_unit = editValues.hashrate_unit;
                break;
              case 'tags':
                updatedDevice.tags = editValues.tags;
                break;
              case 'contract_amount':
                updatedDevice.contract_amount = editValues.contract_amount;
                break;
              case 'model_id':
                updatedDevice.model_id = editValues.model_id;
                updatedDevice.model = editValues.model_name;
                break;
            }
          });
          
          return {
            ...prev,
            device: updatedDevice,
            hosting: updatedHosting,
            location: updatedLocation,
          };
        });
      }

      setOpenEditDialog(false);
    } catch (e) {
      if (e.response && e.response.data && e.response.data.message) {
        showError(getErrorMessage(e));
      } else {
        showError(t('deviceUpdateError'));
      }
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Хелперы для пересчета скорости в TH/s
  const getLatestHashrateValue = () => {
    const arr = deviceData?.hashrate_data;
    if (!arr || !Array.isArray(arr) || arr.length === 0) return null;
    const latest = arr[0]?.hashrate;
    const num = typeof latest === 'number' ? latest : Number(latest);
    return isNaN(num) ? null : num;
  };

  const unitToThMultiplier = (unit) => {
    switch (unit) {
      case 'H/s': return 1e-12;
      case 'kH/s': return 1e-9;
      case 'KH/s': return 1e-9;
      case 'MH/s': return 1e-6;
      case 'Gh/s':
      case 'GH/s': return 1e-3;
      case 'Th/s':
      case 'TH/s': return 1;
      case 'PH/s': return 1e3;
      case 'EH/s': return 1e6;
      default: return 1; // по умолчанию считаем TH/s
    }
  };

  // Функция для отображения предпоказа хешрейта в правильных единицах
  const formatHashratePreview = (thValue, algorithm) => {
    if (algorithm === 'Scrypt') {
      const ghValue = thValue * 1000;
      if (ghValue >= 1e6) {
        const thFromGh = ghValue / 1e6;
        return `${thFromGh.toFixed(2)} TH/s`;
      }
      if (ghValue >= 1e3) {
        const phFromGh = ghValue / 1e3;
        return `${phFromGh.toFixed(2)} PH/s`;
      }
      return `${ghValue.toFixed(2)} GH/s`;
    }
    
    if (algorithm === 'Equihash') {
      const kSolValue = thValue;
      if (kSolValue >= 1e6) {
        const thFromKSol = kSolValue / 1e6;
        return `${thFromKSol.toFixed(2)} TH/s`;
      }
      if (kSolValue >= 1e3) {
        const phFromKSol = kSolValue / 1e3;
        return `${phFromKSol.toFixed(2)} PH/s`;
      }
      return `${kSolValue.toFixed(2)} kSol/s`;
    }
    
    // Для SHA-256, Kaspa и других алгоритмов
    if (thValue >= 1e6) {
      const eh = thValue / 1e6;
      return `${eh.toFixed(2)} EH/s`;
    }
    if (thValue >= 1e3) {
      const ph = thValue / 1e3;
      return `${ph.toFixed(2)} PH/s`;
    }
    return `${thValue.toFixed(2)} TH/s`;
  };

  const fetchEditLists = async () => {
    try {
      const [ownersRes, tagsRes, hostingRes, locationRes, modelsRes] = await Promise.all([
        api.get(`${API_BASE}data/owner`),
        api.get(`${API_BASE}data/tag`),
        api.get(`${API_BASE}data/hosting`),
        api.get(`${API_BASE}data/location`),
        api.get(`${API_BASE}data/models`),
      ]);
      const owners = ownersRes.data?.results || ownersRes.data || [];
      // Обрабатываем теги - они приходят как массив строк в results
      const tags = tagsRes.data?.results || tagsRes.data || [];
      const hostings = hostingRes.data?.results || hostingRes.data || [];
      const locations = locationRes.data?.results || locationRes.data || [];
      const models = modelsRes.data?.results || modelsRes.data || [];
      setOwnersOptions(Array.isArray(owners) ? owners : []);
      setTagsOptions(Array.isArray(tags) ? tags : []);
      setHostingOptions(Array.isArray(hostings) ? hostings : []);
      setLocationOptions(Array.isArray(locations) ? locations : []);
      setModelsOptions(Array.isArray(models) ? models : []);
    } catch (e) {
      // Ошибка загрузки справочников
      showError(t('loadListsError'));
    }
  };

  const fetchTagsList = async () => {
    try {
      const res = await api.get(`${API_BASE}data/tag`);
      // Обрабатываем теги - они приходят как массив строк в results
      const tags = res.data?.results || res.data || [];
      setTagsOptions(Array.isArray(tags) ? tags : []);
    } catch (e) {
      // Ошибка загрузки тегов
    }
  };

  const handleSettingsDialogClose = () => {
    setOpenSettingsDialog(false);
    showInfo(t('settingsSaved'));
  };

  const handleDateRangeDialogOpen = () => {
    setOpenDateRangeDialog(true);
    showInfo(t('dateRangeOpened'));
  };

  const handleDateRangeDialogClose = () => {
    setOpenDateRangeDialog(false);
    showInfo(t('dateRangeClosed'));
  };

  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (value) {
      const fieldName = field === 'start' ? t('dateStart') : t('dateEnd');
      showInfo(t('dateChanged', { field: fieldName, date: value.format('DD.MM.YYYY') }));
    }
  };

  const handleDateRangeApply = () => {
    // Валидация максимального диапазона в 31 день
    if (dateRange.start && dateRange.end) {
      const diffInDays = dateRange.end.diff(dateRange.start, 'day', true);
      
      if (diffInDays > 31) {
        showError(t('dashboard:maxRangeWarning'));
        const newStart = dateRange.end.subtract(31, 'day');
        setDateRange({ start: newStart, end: dateRange.end });
        return;
      }
      
      if (diffInDays < 0) {
        showError(t('dashboard:startAfterEnd'));
        return;
      }
    }
    
    fetchDeviceData();
    fetchChartData();
    void fetchWorkRow();
    setPeriodPreset('custom');
    setOpenDateRangeDialog(false);
    showSuccess(t('dateRangeApplied'));
  };

  const handleQuickDateRange = (type) => {
    const now = dayjs();
    let start, end = now;
    
    switch (type) {
      case '1h':
        start = now.subtract(1, 'hour');
        break;
      case '3h':
        start = now.subtract(3, 'hour');
        break;
      case '6h':
        start = now.subtract(6, 'hour');
        break;
      case '24h':
        start = now.subtract(24, 'hour');
        break;
      case '3d':
        start = now.subtract(3, 'day');
        break;
      case '30d':
        start = now.subtract(30, 'day');
        break;
      case '90d':
        start = now.subtract(90, 'day');
        break;
      case '180d':
        start = now.subtract(180, 'day');
        break;
      default:
        return;
    }
    
    setDateRange({ start, end });
  };

  // Функция для форматирования диапазона дат в удобочитаемый вид
  const formatDateRange = () => {
    if (!dateRange.start || !dateRange.end) return t('dashboard:selectRange');

    const start = dateRange.start;
    const end = dateRange.end;
    const now = dayjs();

    // Вычисляем разницу
    const diffInHours = end.diff(start, 'hour', true);
    const diffInDays = end.diff(start, 'day', true);

    // Проверяем предустановленные диапазоны (с допуском ±5 минут)
    if (Math.abs(diffInHours - 1) < 0.1) return t('dashboard:range1h');
    if (Math.abs(diffInHours - 3) < 0.1) return t('dashboard:range3h');
    if (Math.abs(diffInHours - 6) < 0.1) return t('dashboard:range6h');
    if (Math.abs(diffInHours - 24) < 0.1) return t('dashboard:range24h');
    if (Math.abs(diffInDays - 3) < 0.02) return t('dashboard:range3d');
    if (Math.abs(diffInDays - 30) < 0.02) return t('dashboard:range30d');
    if (Math.abs(diffInDays - 90) < 0.02) return t('dashboard:range90d');
    if (Math.abs(diffInDays - 180) < 0.02) return t('dashboard:range180d');

    // Для кастомных диапазонов показываем даты
    if (diffInDays < 1) {
      // Меньше суток - показываем часы
      return t('dashboard:rangeCustom', { start: start.format('DD.MM HH:mm'), end: end.format('HH:mm') });
    } else if (diffInDays <= 7) {
      // До недели - показываем даты с временем
      return t('dashboard:rangeCustom', { start: start.format('DD.MM HH:mm'), end: end.format('DD.MM HH:mm') });
    } else {
      // Больше недели - показываем только даты
      return t('dashboard:rangeCustomDays', { start: start.format('DD.MM'), end: end.format('DD.MM'), days: Math.round(diffInDays) });
    }
  };

  const handleModuleToggle = (module) => {
    setVisibleModules((prev) => {
      const updatedModules = {
        ...prev,
        [module]: !prev[module],
      };
      // Сохраняем настройки в localStorage
      localStorage.setItem('deviceVisibleModules', JSON.stringify(updatedModules));
      return updatedModules;
    });
    
    const isVisible = !visibleModules[module];
    const moduleNames = {
      info: t('moduleInfo'),
      graphDevice: t('moduleGraph'),
      worktime: t('moduleWorktime'),
      temperature: t('moduleTemp'),
      fan: t('moduleFan'),
      share: t('moduleShare'),
      pool: t('modulePool'),
      logs: t('moduleLogs'),
      hashrate: t('moduleHashrate')
    };

    showInfo(t('moduleToggle', { name: moduleNames[module] || module, state: isVisible ? t('moduleEnabled') : t('moduleDisabled') }));
  };

  const handleSelectAll = (event) => {
    const isChecked = event.target.checked;
    setSelectAll(isChecked);
    if (isChecked) {
      setSelectedRows(deviceRows.map((_, index) => index));
      showInfo(t('selectedDevices', { count: deviceRows.length }));
    } else {
      setSelectedRows([]);
      showInfo(t('selectionCleared'));
    }
  };

  const handleRowSelect = (index) => {
    setSelectedRows((prevSelectedRows) => {
      if (prevSelectedRows.includes(index)) {
        return prevSelectedRows.filter((rowIndex) => rowIndex !== index);
      } else {
        return [...prevSelectedRows, index];
      }
    });
    
    const isSelected = selectedRows.includes(index);
    const deviceName = deviceRows[index]?.model || `${t('dashboard:columnDevice')} ${index + 1}`;
    showInfo(isSelected ? t('deviceRemoved', { name: deviceName }) : t('deviceAdded', { name: deviceName }));
  };

  // Возраст последнего измерения в карточке: старше 10 минут — значения не текущие
  const latestAt = deviceData?.latest_metrics?.temperature?.created_at || null;
  const latestStale = !latestAt || Date.now() - new Date(latestAt).getTime() > 10 * 60 * 1000;
  const formatLatestAt = (iso) => {
    const d = new Date(iso);
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getDate())}.${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
  };

  const getStatusStyle = (status) => {
    if (status === 'Online') {
      return {
        backgroundColor: '#4caf50',
        color: '#fff',
        borderRadius: '12px',
        padding: '4px 8px',
        textAlign: 'center',
      };
    } else if (status.includes('Offline')) {
      return {
        backgroundColor: '#f44336',
        color: '#fff',
        borderRadius: '12px',
        padding: '4px 8px',
        textAlign: 'center',
      };
    }
    return {};
  };

  const shareData = [
    { accepted: 120, rejected: 30 },
    { accepted: 150, rejected: 50 },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div style={{ backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
        {/* CSS стили для анимации */}
        <style>
          {`
            @keyframes pulse {
              0% {
                opacity: 1;
                transform: scale(1);
              }
              50% {
                opacity: 0.5;
                transform: scale(1.2);
              }
              100% {
                opacity: 1;
                transform: scale(1);
              }
            }
          `}
        </style>
        
        <Menu 
          style={{ position: 'relative', zIndex: 10 }} 
          onRefresh={refreshFunctions} 
          refreshInterval={refreshInterval}
        />
        <Container 
          maxWidth="lg" 
          style={{ 
            backgroundColor: theme.palette.background.default, 
            padding: '0px',
            position: 'relative'
          }}
        >
          {deviceData && (
            <DeviceCard
              deviceData={deviceData}
              chartData={chartData}
              alertRules={alertRules.length > 0 ? alertRules[0] : null}
              dateRange={dateRange}
              periodPreset={periodPreset}
              onPresetChange={handlePresetChange}
              onCustomPeriod={handleDateRangeDialogOpen}
              summary={workRow}
              kwhPrice={kwhPrice}
              outages={outages}
              events={deviceEvents}
              statusInfo={(() => {
                const status = getDeviceStatus(deviceData.uptime_data);
                const startedAt = status === true || status === false ? getStatusStartedAt(deviceData.uptime_data, status) : null;
                return { status, duration: startedAt ? getStatusDuration(startedAt) : null };
              })()}
              latestStale={latestStale}
              latestAt={latestAt}
              canEdit={isAdminOrManager}
              onEdit={handleInfoEditOpen}
              onThresholds={handleValuesSettingsOpen}
              onBack={() => navigate('/dashboard')}
              logsNode={(
                <Logs logsData={{ logs: deviceLogs, loading: deviceLogsLoading, onLoadMore: handleLoadMoreDeviceLogs, canLoadMore: deviceLogs.length < deviceLogsTotal }} />
              )}
            />
          )}
          
          {/* Диалог настроек модулей */}
          <Dialog open={openSettingsDialog} onClose={handleSettingsDialogClose} maxWidth="sm" fullWidth>
            <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{t('settingsTitle')}</span>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={() => {
                  // Сбрасываем настройки к значениям по умолчанию
                  const defaultModules = {
                    info: true,
                    graphDevice: true,
                    worktime: true,
                    temperature: true,
                    fan: true,
                    share: true,
                    pool: true,
                    logs: true,
                    hashrate: false,
                  };
                  setVisibleModules(defaultModules);
                  // Удаляем настройки из localStorage
                  localStorage.removeItem('deviceVisibleModules');
                  // Закрываем диалог
                  handleSettingsDialogClose();
                  // Перезагружаем страницу
                  window.location.reload();
                }}
              >
                {t('settingsReset')}
              </Button>
            </DialogTitle>
            <DialogContent>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={visibleModules.info}
                      onChange={() => handleModuleToggle('info')}
                    />
                  }
                  label={t('settingsDeviceInfo')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={visibleModules.graphDevice}
                      onChange={() => handleModuleToggle('graphDevice')}
                    />
                  }
                  label={t('settingsHashrateGraph')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={visibleModules.worktime}
                      onChange={() => handleModuleToggle('worktime')}
                    />
                  }
                  label={t('settingsWorktime')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={visibleModules.temperature}
                      onChange={() => handleModuleToggle('temperature')}
                    />
                  }
                  label={t('settingsTemperature')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={visibleModules.fan}
                      onChange={() => handleModuleToggle('fan')}
                    />
                  }
                  label={t('settingsFans')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={visibleModules.share}
                      onChange={() => handleModuleToggle('share')}
                    />
                  }
                  label={t('settingsShares')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={visibleModules.pool}
                      onChange={() => handleModuleToggle('pool')}
                    />
                  }
                  label={t('settingsPools')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={visibleModules.logs}
                      onChange={() => handleModuleToggle('logs')}
                    />
                  }
                  label={t('settingsLogs')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={visibleModules.hashrate}
                      onChange={() => handleModuleToggle('hashrate')}
                    />
                  }
                  label={t('settingsHashrateTable')}
                />
              </FormGroup>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleSettingsDialogClose}>{t('common:close')}</Button>
            </DialogActions>
          </Dialog>
          
          {/* Диалог выбора диапазона дат */}
          <Dialog open={openDateRangeDialog} onClose={handleDateRangeDialogClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('dateRangeTitle')}</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '10px' }}>
                {/* Быстрый выбор предустановленных диапазонов */}
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '8px', 
                  justifyContent: 'center',
                  marginBottom: '16px'
                }}>
                  <Button size="small" variant="outlined" onClick={() => handleQuickDateRange('1h')}>{t('dashboard:btn1h')}</Button>
                  <Button size="small" variant="outlined" onClick={() => handleQuickDateRange('3h')}>{t('dashboard:btn3h')}</Button>
                  <Button size="small" variant="outlined" onClick={() => handleQuickDateRange('6h')}>{t('dashboard:btn6h')}</Button>
                  <Button size="small" variant="outlined" onClick={() => handleQuickDateRange('24h')}>{t('dashboard:btn24h')}</Button>
                  <Button size="small" variant="outlined" onClick={() => handleQuickDateRange('3d')}>{t('dashboard:btn3d')}</Button>
                  <Button size="small" variant="outlined" onClick={() => handleQuickDateRange('30d')}>{t('dashboard:btn30d')}</Button>
                  <Button size="small" variant="outlined" disabled>{t('dashboard:btn90d')}</Button>
                  <Button size="small" variant="outlined" disabled>{t('dashboard:btn180d')}</Button>
                </div>
                
                <Typography variant="caption" color="text.secondary" style={{ textAlign: 'center', marginBottom: '8px' }}>
                  {t('dashboard:maxRange')}
                </Typography>
                
                {/* Календарь для выбора диапазона */}
                <div style={{ 
                  border: `1px solid ${theme.palette.divider}`, 
                  borderRadius: '8px', 
                  padding: '16px',
                  backgroundColor: theme.palette.background.paper
                }}>
                  <DatePicker
                    label={t('dashboard:startDate')}
                    value={dateRange.start}
                    onChange={(v) => v && handleDateRangeChange('start', v)}
                    format="DD.MM.YYYY"
                    slotProps={{ textField: { fullWidth: true, size: 'medium' } }}
                  />
                  
                  <div style={{ margin: '16px 0', textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      {t('dashboard:to')}
                    </Typography>
                  </div>
                  
                  <DatePicker
                    label={t('dashboard:endDate')}
                    value={dateRange.end}
                    onChange={(v) => v && handleDateRangeChange('end', v)}
                    format="DD.MM.YYYY"
                    slotProps={{ textField: { fullWidth: true, size: 'medium' } }}
                  />
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDateRangeDialogClose}>{t('common:cancel')}</Button>
              <Button 
                onClick={handleDateRangeApply} 
                variant="contained" 
                color="primary"
                disabled={!dateRange.start || !dateRange.end}
              >
                {t('common:apply')}
              </Button>
            </DialogActions>
          </Dialog>
          {/* Диалог редактирования информации устройства */}
          <Dialog open={openEditDialog} onClose={handleEditCancel} maxWidth="sm" fullWidth>
            <DialogTitle>{t('editInfoTitle')}</DialogTitle>
            <DialogContent>
              {editValues && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                  <Typography variant="body2" color="textSecondary">{t('editInfoHint')}</Typography>
                  <TextField size="small" label="mac" value={editValues.mac} onChange={(e) => setEditValues(v => ({ ...v, mac: e.target.value }))} fullWidth />
                  <TextField size="small" label="sn" value={editValues.serial_number} onChange={(e) => setEditValues(v => ({ ...v, serial_number: e.target.value }))} fullWidth />
                  <TextField size="small" label={t('editPsuSn')} value={editValues.sn} onChange={(e) => setEditValues(v => ({ ...v, sn: e.target.value }))} fullWidth />
                  <TextField size="small" label={t('editContractAmount')} value={editValues.contract_amount ?? ''} onChange={(e) => setEditValues(v => ({ ...v, contract_amount: e.target.value }))} fullWidth />

                  {/* Владелец */}
                  <Autocomplete
                    options={ownersOptions}
                    getOptionLabel={(o) => {
                      if (!o) return '';
                      return o?.email ? `${o.name} (${o.email})` : (o?.name || '');
                    }}
                    value={ownersOptions.find(o => o?.id === editValues.owner_id) || null}
                    onChange={(_, val) => setEditValues(v => ({ ...v, owner_id: val?.id || '', owner_name: val?.name || '' }))}
                    renderInput={(params) => <TextField {...params} size="small" label={t('editOwner')} fullWidth />}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2">{option?.name || ''}</Typography>
                          {option?.email && (
                            <Typography variant="caption" color="text.secondary">
                              {option.email}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                  />

                  {/* Локация */}
                  <Autocomplete
                    options={locationOptions}
                    getOptionLabel={(o) => o?.name || ''}
                    value={locationOptions.find(o => o?.id === editValues.location_id) || null}
                    onChange={(_, val) => setEditValues(v => ({ ...v, location_id: val?.id || '', location_name: val?.name || '' }))}
                    renderInput={(params) => <TextField {...params} size="small" label={t('editLocation')} fullWidth />}
                  />

                  {/* Хостинг */}
                  <Autocomplete
                    options={hostingOptions}
                    getOptionLabel={(o) => o?.name || ''}
                    value={hostingOptions.find(o => o?.id === editValues.hosting_id) || null}
                    onChange={(_, val) => setEditValues(v => ({ ...v, hosting_id: val?.id || '', hosting_name: val?.name || '' }))}
                    renderInput={(params) => <TextField {...params} size="small" label={t('editHosting')} fullWidth />}
                  />

                  {/* Модель */}
                  <Autocomplete
                    options={modelsOptions}
                    getOptionLabel={(o) => {
                      if (!o) return '';
                      return `${o.model || ''} (${o.algorithm || ''}, ${o.hashrate_unit || ''})`;
                    }}
                    value={modelsOptions.find(o => o?.id === editValues.model_id) || null}
                    onChange={(_, val) => {
                      setEditValues(v => ({
                        ...v,
                        model_id: val?.id || '',
                        model_name: val?.model || '',
                        algorithm: val?.algorithm || v.algorithm,
                        hashrate_unit: val?.hashrate_unit || v.hashrate_unit
                      }));
                    }}
                    renderInput={(params) => <TextField {...params} size="small" label={t('editModel')} fullWidth />}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2">{option?.model || ''}</Typography>
                          {(option?.algorithm || option?.hashrate_unit) && (
                            <Typography variant="caption" color="text.secondary">
                              {option?.algorithm} • {option?.hashrate_unit}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                  />

                  {/* Алгоритм */}
                  <Autocomplete
                    options={[
                      { id: 'SHA-256', name: 'SHA-256' }, 
                      { id: 'Scrypt', name: 'Scrypt' }, 
                      { id: 'X11', name: 'X11' },
                      { id: 'Sia', name: 'Sia' },
                      { id: 'Kaspa', name: 'Kaspa' },
                      { id: 'Equihash', name: 'Equihash' }
                    ]}
                    getOptionLabel={(o) => o?.name}
                    value={{ id: editValues.algorithm || '', name: editValues.algorithm || '' }}
                    onChange={(_, val) => {
                      const algorithm = val?.id || '';
                      // Автоматически выбираем единицу измерения на основе алгоритма
                      let defaultUnit = 'TH/s';
                      if (algorithm === 'Scrypt') {
                        defaultUnit = 'MH/s';
                      } else if (algorithm === 'Equihash') {
                        defaultUnit = 'TH/s'; // Для Equihash используем TH/s, но показываем в kSol/s
                      }
                      setEditValues(v => ({ 
                        ...v, 
                        algorithm: algorithm,
                        hashrate_unit: defaultUnit
                      }));
                    }}
                    renderInput={(params) => <TextField {...params} size="small" label={t('editAlgorithm')} fullWidth />}
                  />

                  {/* hashrate unit + пересчет в TH/s */}
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <Autocomplete
                      options={[
                        'H/s','kH/s','MH/s','GH/s','TH/s','PH/s','EH/s'
                      ]}
                      getOptionLabel={(o) => o}
                      value={editValues.hashrate_unit || 'TH/s'}
                      onChange={(_, val) => setEditValues(v => ({ ...v, hashrate_unit: val || 'TH/s' }))}
                      renderInput={(params) => <TextField {...params} size="small" label={t('editHashrateUnit')} fullWidth />}
                      style={{ flex: 1 }}
                    />
                    <div style={{ whiteSpace: 'nowrap' }}>
                      {(() => {
                        const latest = getLatestHashrateValue();
                        if (latest == null) return '—';
                        const th = latest * unitToThMultiplier(editValues.hashrate_unit || 'TH/s');
                        return formatHashratePreview(th, editValues.algorithm);
                      })()}
                    </div>
                  </div>

                  {/* Теги */}
                  <Autocomplete
                    multiple
                    freeSolo
                    options={tagsOptions}
                    value={Array.isArray(editValues.tags) ? editValues.tags : []}
                    onChange={(_, val) => {
                      setEditValues(v => ({ ...v, tags: val }));
                    }}
                    onOpen={() => {
                      fetchTagsList();
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField {...params} size="small" label={t('editTagsInput')} placeholder={t('editTagPlaceholder')} />
                    )}
                  />
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleEditCancel} disabled={isSavingEdit}>{t('common:cancel')}</Button>
              <Button onClick={handleEditSave} variant="contained" color="primary" disabled={isSavingEdit}>
                {isSavingEdit ? t('saving') : t('common:save')}
              </Button>
            </DialogActions>
          </Dialog>
          {/* Диалог настройки значений */}
          <Dialog open={openValuesDialog} onClose={handleValuesSettingsClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('valuesTitle')}</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                <Typography variant="subtitle2" color="textSecondary">{t('valuesHashrate')}</Typography>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <TextField
                    size="small"
                    type="number"
                    label="hashrate_min"
                    value={valuesForm.hashrate_min ?? ''}
                    onChange={(e) => handleValuesInputChange('hashrate_min', e.target.value)}
                    fullWidth
                  />
                  <Box sx={{
                    minWidth: '120px',
                    textAlign: 'center',
                    padding: '8px 12px',
                    backgroundColor: 'action.hover',
                    borderRadius: '4px',
                    fontWeight: 'bold',
                    fontSize: '0.875rem'
                  }}>
                    {deviceData?.latest_metrics?.temperature?.hashrate != null
                      ? Math.floor(deviceData.latest_metrics.temperature.hashrate).toString()
                      : '—'}
                  </Box>
                  <TextField
                    size="small"
                    type="number"
                    label="hashrate_max"
                    value={valuesForm.hashrate_max ?? ''}
                    onChange={(e) => handleValuesInputChange('hashrate_max', e.target.value)}
                    fullWidth
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const currentHashrate = deviceData?.latest_metrics?.temperature?.hashrate;
                      if (currentHashrate != null) {
                        const hashrate = Math.floor(currentHashrate);
                        const minValue = Math.floor(hashrate * 0.9);
                        const maxValue = Math.floor(hashrate * 1.1);
                        setValuesForm((prev) => ({
                          ...prev,
                          hashrate_min: minValue,
                          hashrate_max: maxValue
                        }));
                      }
                    }}
                    sx={{ minWidth: '70px' }}
                  >
                    ±10%
                  </Button>
                </div>

                <Divider />
                <Typography variant="subtitle2" color="textSecondary">{t('valuesTemps')}</Typography>
                {[0,1,2,3,4,5].map((i) => {
                  const currentTemp = deviceData?.latest_metrics?.temperature?.[`temperature${i}`];
                  const hasValue = currentTemp != null;

                  return (
                    <div key={`t-${i}`} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <TextField
                        size="small"
                        type="number"
                        label={`temperature${i}_min`}
                        value={valuesForm[`temperature${i}_min`] ?? ''}
                        onChange={(e) => handleValuesInputChange(`temperature${i}_min`, e.target.value)}
                        fullWidth
                      />
                      <Box sx={{
                        minWidth: '70px',
                        textAlign: 'center',
                        padding: '8px 12px',
                        backgroundColor: hasValue ? 'action.hover' : 'action.disabledBackground',
                        borderRadius: '4px',
                        fontWeight: hasValue ? 'bold' : 'normal',
                        fontSize: '0.875rem',
                        color: hasValue ? 'text.primary' : 'text.disabled'
                      }}>
                        {hasValue ? `${Math.floor(currentTemp)}°C` : '—'}
                      </Box>
                      <TextField
                        size="small"
                        type="number"
                        label={`temperature${i}_max`}
                        value={valuesForm[`temperature${i}_max`] ?? ''}
                        onChange={(e) => handleValuesInputChange(`temperature${i}_max`, e.target.value)}
                        fullWidth
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          if (hasValue) {
                            const temp = Math.floor(currentTemp);
                            const minValue = 0; // Минимум всегда 0 для мониторинга перегрева
                            const maxValue = Math.floor(temp * 1.1);
                            setValuesForm((prev) => ({
                              ...prev,
                              [`temperature${i}_min`]: minValue,
                              [`temperature${i}_max`]: maxValue
                            }));
                          }
                        }}
                        disabled={!hasValue}
                        sx={{ minWidth: '70px' }}
                      >
                        ±10%
                      </Button>
                    </div>
                  );
                })}

                <Divider />
                <Typography variant="subtitle2" color="textSecondary">{t('valuesFans')}</Typography>
                {[0,1,2,3].map((i) => {
                  const currentFan = deviceData?.latest_metrics?.fans?.[`fan_speed${i}`];
                  const hasValue = currentFan != null;

                  return (
                    <div key={`f-${i}`} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <TextField
                        size="small"
                        type="number"
                        label={`fan_speed${i}_min`}
                        value={valuesForm[`fan_speed${i}_min`] ?? ''}
                        onChange={(e) => handleValuesInputChange(`fan_speed${i}_min`, e.target.value)}
                        fullWidth
                      />
                      <Box sx={{
                        minWidth: '80px',
                        textAlign: 'center',
                        padding: '8px 12px',
                        backgroundColor: hasValue ? 'action.hover' : 'action.disabledBackground',
                        borderRadius: '4px',
                        fontWeight: hasValue ? 'bold' : 'normal',
                        fontSize: '0.875rem',
                        color: hasValue ? 'text.primary' : 'text.disabled'
                      }}>
                        {hasValue ? `${Math.floor(currentFan)} RPM` : '—'}
                      </Box>
                      <TextField
                        size="small"
                        type="number"
                        label={`fan_speed${i}_max`}
                        value={valuesForm[`fan_speed${i}_max`] ?? ''}
                        onChange={(e) => handleValuesInputChange(`fan_speed${i}_max`, e.target.value)}
                        fullWidth
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          if (hasValue) {
                            const fan = Math.floor(currentFan);
                            const minValue = 0; // Минимум всегда 0 для мониторинга остановки вентиляторов
                            const maxValue = Math.floor(fan * 1.1);
                            setValuesForm((prev) => ({
                              ...prev,
                              [`fan_speed${i}_min`]: minValue,
                              [`fan_speed${i}_max`]: maxValue
                            }));
                          }
                        }}
                        disabled={!hasValue}
                        sx={{ minWidth: '70px' }}
                      >
                        ±10%
                      </Button>
                    </div>
                  );
                })}
              </div>
            </DialogContent>
            <DialogActions>
              {valuesExisting && (
                <Button onClick={handleValuesDelete} color="error" disabled={isDeletingValues}>
                  {isDeletingValues ? t('deleting') : t('common:delete')}
                </Button>
              )}
              <Button onClick={handleValuesSettingsClose}>
                {t('common:cancel')}
              </Button>
              <Button onClick={handleValuesSave} variant="contained" color="primary" disabled={isSavingValues}>
                {isSavingValues ? t('saving') : t('common:save')}
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
        <NotificationContainer />
      </div>
    </LocalizationProvider>
  );
};

export default Device;
